import { useCallback, useEffect, useMemo, useState } from 'react';

import { initButtonProps } from 'helpers/buttons';

import {
	ActionTypes,
	CopyProjectDetails,
	CopyProjectElements,
	ProjectFormValues,
	ProjectMetadataDefinition,
	ProjectMetadataType
} from 'store/data/projects';
import { InputType } from 'types/index';
import { Column, Row } from '../CreateAndImport/CreateProjectForm/CreateProjectForm.style';
import { CheckboxContainer, FormContainer } from './CopyProjectModal.style';
import { Modal } from 'components/UI/Modal';
import { Input } from 'components/UI/Inputs/Input';
import { Spacer } from 'components/UI/Spacer';
import { Typography } from 'components/UI/Typography';
import { Checkbox } from 'components/UI/Interactables/Checkbox';
import { StickyFooter } from 'components/UI/StickyFooter';
import {
	useCopiedProjectId,
	useCopyProject,
	useProjectById,
	useProjectMetadataDefinition,
	useScopedProjects,
	useTransactionActivity,
	useTranslation,
	useUpdateProject,
	useUsername
} from 'hooks/store';
import { InputLabel } from 'components/UI/Inputs/InputLabel';
import { Flex } from 'components/UI/Flex';
import { Button } from 'components/UI/Interactables/Button';
import { ChangeOwnerModal } from '../ChangeOwnerModal/ChangeOwnerModal';
import { useCompletedAction } from 'hooks/utils';
import { UserCard } from 'components/UI/UserCard';
import { ProjectStatus, ProjectType } from 'types/data/projects/constants';
import { useTracking } from 'app/tracking/TrackingProvider';
import { useQuery } from '@tanstack/react-query';
import { decodeTokenPayload } from 'features/communication-bridge/tokenStore';
import {
	getInitialMetadataFormValues,
	transforMetadataParametersToFormRows,
	validateProjectForm
} from '../../../helpers/projects/projectMetadata';
import { InfoTooltip } from '../../UI/Interactables/InfoTooltip';
import { CreatableSelect } from '../../UI/Interactables/CreatableSelect';
import { Controller, useForm } from 'react-hook-form';
import { Form } from '../../../hooks/ui/useReactForm';
import { parseToApiMetadata } from '../../../store/data/projects/parsers';

const firstLetterToUpperCase = (label: string) => {
	return label.charAt(0).toUpperCase() + label.slice(1);
};
interface Props {
	projectId: string;
	onClose: (success?: boolean) => void;
}

enum CheckboxOptions {
	Variables = 'variables',
	Groups = 'groups',
	Forms = 'forms',
	Dependencies = 'dependencies',
	Statuses = 'statuses',
	StatusPermissions = 'statusPermissions',
	Series = 'series',
	Data = 'data',
	ProjectRoles = 'projectRoles'
}

export type CopyProjectNewOwner = {
	emailAddress: string;
	userId: string;
	userFirstName?: string;
	userSirName?: string;
	organization?: string;
};

export function CopyProjectModal({ projectId, onClose }: Props) {
	const { translate } = useTranslation();
	const { track } = useTracking();

	const { data: isDatasetSuperAdmin } = useQuery({
		queryKey: ['isDatasetSuperAdmin'],
		queryFn: async () => {
			try {
				const payload = decodeTokenPayload();
				return payload?.['custom:datasetRole'] === 'datasetSuperAdmin';
			} catch {
				return false;
			}
		}
	});

	const [, getProjects] = useScopedProjects();
	const [, setCopiedProjectId] = useCopiedProjectId();
	const project = useProjectById(projectId);
	const userId = useUsername();
	const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
	const [endDateError, setEndDateError] = useState<string>(() => {
		if (!project || !project.projectEndDate) return '';
		const date = new Date(project.projectEndDate);

		date.setHours(0, 0, 0, 0);

		const now = new Date(new Date().setHours(0, 0, 0, 0));

		if (date < now) {
			return translate(dict => dict.createProject.projectEndDateError);
		}

		return '';
	});

	const [projectOwner, setProjectOwner] = useState<CopyProjectNewOwner>({
		userFirstName: project?.projectOwnerDetails?.userFirstName || '',
		userSirName: project?.projectOwnerDetails?.userSirName || '',
		userId: project?.projectOwnerDetails?.userId || '',
		emailAddress: project?.projectOwnerDetails?.emailAddress || '',
		organization: project?.projectOwnerDetails?.organization || ''
	});

	const isSameOwner = useMemo(
		() => projectOwner.userId === project?.projectOwnerDetails?.userId,
		[projectOwner, project]
	);

	const isProjectOwnerInHSO =
		projectOwner.organization === 'HSØ' || projectOwner.organization === 'OUSHF';

	const canEditMetadata = isSameOwner && isProjectOwnerInHSO;

	useEffect(() => {
		handleAutoSelectionRules();
		if (!isSameOwner) resetForm();
	}, [isSameOwner]);

	const [
		{ loading: copyingProject, data: copiedProjectId, fetched: isFetchedCopiedProjectId },
		copyProject
	] = useCopyProject();
	const [{ loading: updatingProject, error: errorUpdatingProject }, updateProject] =
		useUpdateProject();

	const [{ loading: copyingData }] = useTransactionActivity(ActionTypes.COPY_PROJECT);

	const [isElementChecked, setIsElementChecked] = useState<CopyProjectElements>({
		variables: { checked: false, disabled: false, visible: true },
		groups: { checked: false, disabled: false, visible: true },
		forms: { checked: false, disabled: false, visible: true },
		dependencies: { checked: false, disabled: false, visible: true },
		statuses: { checked: false, disabled: false, visible: true },
		statusPermissions: { checked: false, disabled: false, visible: true },
		series: { checked: false, disabled: false, visible: true },
		data: { checked: false, disabled: false, visible: isDatasetSuperAdmin ?? false },
		// 'projectRoles' should be added back after we settle on a solution for PRJCTS-8389
		projectRoles: { checked: false, disabled: false, visible: false }
	});

	const [{ data: projectMetadataDefinitionOwner }] = useProjectMetadataDefinition();
	const projectMetadataDefinition = isProjectOwnerInHSO
		? projectMetadataDefinitionOwner
		: ({} as ProjectMetadataDefinition);
	const initialMetadataValues = getInitialMetadataFormValues(projectMetadataDefinition, project);
	const initialValues: ProjectFormValues = {
		projectId: projectId,
		projectName: project?.projectName || '',
		givenProjectNumber: project?.givenProjectNumber || '',
		status: project?.status || ProjectStatus.Ongoing,
		projectType: project?.projectType || ProjectType.CORE,
		projectEndDate: project?.projectEndDate || '',
		description: project?.description || '',
		slideFolderURL: project?.slideFolderURL || '',
		...initialMetadataValues
	};

	const [textAreaCount, setTextAreaCount] = useState(initialValues.description.toString().length);

	const {
		register,
		handleSubmit,
		setValue,
		formState: { isDirty, isValid },
		getValues,
		reset: resetForm,
		control,
		watch
	} = useForm({
		defaultValues: initialValues,
		reValidateMode: 'onChange',
		mode: 'onChange',
		criteriaMode: 'all'
	});

	const { errors, requiredFields } = validateProjectForm(
		projectMetadataDefinition,
		watch,
		translate
	);
	const formHasChanges = isDirty;
	const formHasErrors = Object.keys(errors).length > 0;

	const handleSelectedElement = (element: string) => {
		isElementChecked[element].checked = !isElementChecked[element].checked;
		handleAutoSelectionRules();
	};

	const handleAutoSelectionRules = () => {
		if (
			isElementChecked.forms.checked === true ||
			isElementChecked.dependencies.checked === true ||
			isElementChecked.statuses.checked === true ||
			isElementChecked.statusPermissions.checked === true ||
			isElementChecked.data.checked === true ||
			isElementChecked.projectRoles.checked === true
		) {
			setIsElementChecked(prevState => ({
				...prevState,
				variables: { ...prevState.variables, checked: true, disabled: true },
				groups: { ...prevState.groups, checked: true, disabled: true },
				statuses: {
					...prevState.statuses,
					checked:
						isElementChecked.statusPermissions.checked === true
							? true
							: prevState.statuses.checked,
					disabled: isElementChecked.statusPermissions.checked === true ? true : false
				},
				series: { ...prevState.series, checked: true, disabled: true }
			}));
		} else {
			setIsElementChecked(prevState => ({
				...prevState,
				variables: { ...prevState.variables, disabled: false },
				groups: { ...prevState.groups, disabled: false },
				series: { ...prevState.series, disabled: false }
			}));
		}
		if (isElementChecked.groups.checked === true) {
			setIsElementChecked(prevState => ({
				...prevState,
				variables: { ...prevState.variables, checked: true, disabled: true }
			}));
		}

		if (isElementChecked.series.checked) {
			setIsElementChecked(prevState => ({
				...prevState,
				variables: { ...prevState.variables, checked: true, disabled: true },
				groups: { ...prevState.groups, checked: true, disabled: true }
			}));
		}

		setIsElementChecked(prevState => ({
			...prevState,
			data: !isSameOwner
				? { ...prevState.data, checked: false, disabled: true }
				: { ...prevState.data, disabled: false }
		}));
	};
	const values = { ...getValues() };

	const calculateLetters = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setTextAreaCount(e.target.value.length);
		if (e.target.value.length <= 0) {
			setTextAreaCount(initialValues.description.toString().length);
		}
	};

	const schema = projectMetadataDefinition?.metadataSchema;

	const handleFormSubmit = handleSubmit(() => {
		trimFields();

		const values = { ...getValues() };
		const parsedValues = { ...values };

		if (isSameOwner) {
			if (
				!schema?.if?.properties.projectType.enum.includes(
					String(values['metadata_projectType'])
				)
			) {
				parsedValues['metadata_archiveNumber'] =
					values['metadata_archiveNumber']?.toString() ?? '';
			}
		} else if (isProjectOwnerInHSO) {
			// HSØ and not same owner
			parsedValues['metadata_projectType'] = '3';
			parsedValues['metadata_archiveNumber'] = '';
		} else {
			parsedValues['metadata_projectType'] = '';
			parsedValues['metadata_archiveNumber'] = '';
		}

		copyProject({
			projectId,
			projectDefinition: initialValues.projectName.toString().length > 0,
			variables: isElementChecked.variables.checked,
			groups: isElementChecked.groups.checked,
			dependencies: isElementChecked.dependencies.checked,
			statuses: isElementChecked.statuses.checked,
			statusPermissions: isElementChecked.statusPermissions.checked,
			series: isElementChecked.series.checked,
			forms: isElementChecked.forms.checked,
			data: isElementChecked.data.checked,
			projectRoles: isElementChecked.projectRoles.checked,
			newOwnerId: !isSameOwner ? projectOwner.userId : undefined,
			newProjectDetails: parseFormValuesToProject(parsedValues, projectMetadataDefinition)
		});
		track({
			eventName: 'project_copied',
			data: {
				project_id: projectId,
				variables: isElementChecked.variables.checked,
				groups: isElementChecked.groups.checked,
				dependencies: isElementChecked.dependencies.checked,
				statuses: isElementChecked.statuses.checked,
				statusPermissions: isElementChecked.statusPermissions.checked,
				series: isElementChecked.series.checked,
				forms: isElementChecked.forms.checked,
				data: isElementChecked.data.checked,
				projectRoles: isElementChecked.projectRoles.checked,
				isSameOwner
			}
		});
	});

	useCompletedAction(updatingProject, errorUpdatingProject, () => {
		onFinish();
	});

	useEffect(() => {
		if (isFetchedCopiedProjectId && copiedProjectId && !copyingData) {
			onFinish();
		}
	}, [isFetchedCopiedProjectId, copiedProjectId, isSameOwner]);

	function trimFields() {
		const values = { ...getValues() };

		setValue('projectName', values.projectName.toString().trim());
		setValue('description', values.description.toString().trim());
		setValue('givenProjectNumber', values.givenProjectNumber.toString().trim());
	}

	const buttonProps = initButtonProps(buttons => {
		buttons.primary = {
			label: translate(({ copyProject }) => copyProject.copyProjectButton),
			disabled: formHasChanges ? !isValid || formHasErrors : false,
			loading: copyingProject || updatingProject,
			onClick: handleFormSubmit
		};

		buttons.neutral = {
			label: translate(({ buttons }) => buttons.cancel),
			onClick: onClose
		};
	});

	function onFinish() {
		if (isSameOwner) getProjects();
		setCopiedProjectId(null);
		onClose();
	}

	function handleClose() {
		if (copiedProjectId) {
			if (isSameOwner) getProjects();
			setCopiedProjectId(null);
		}
		onClose();
	}

	function checkProjectEndDate(dateStr: string) {
		if (dateStr === '' && endDateError !== '') {
			setEndDateError('');
			return;
		}
		const date = new Date(dateStr);

		date.setHours(0, 0, 0, 0);

		const now = new Date(new Date().setHours(0, 0, 0, 0));

		if (date < now) {
			setEndDateError(translate(dict => dict.createProject.projectEndDateError));
		} else {
			if (endDateError.length > 0) {
				setEndDateError('');
			}
		}
	}
	const formMetadataParameters = transforMetadataParametersToFormRows(projectMetadataDefinition);
	const handleTrimOnBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const { name, value } = e.target;

			setValue(name, value.trim());
		},
		[]
	);

	return (
		<Modal
			data-test-id="confirmCopyProject"
			title={translate(dict => dict.copyProject.copyProject)}
			onClose={handleClose}
			enterAsPrimaryOnClick
			visible
			close
			size={s => s.full}
			fullSizeConfig={{
				narrow: true,
				centerTitle: true
			}}
		>
			<FormContainer justify={j => j.between} column>
				<Form onSubmit={handleFormSubmit}>
					<Input
						{...register('projectName')}
						type={InputType.Text}
						label={translate(dict => dict.copyProject.newProjectTitle)}
						error={errors.projectName?.message}
						autoFocus
						placeholder={values.projectName.toString()}
						readOnly={!isSameOwner}
						onBlur={handleTrimOnBlur}
					/>
					<Spacer size={s => s.s} />
					<Input
						{...register('description')}
						type={InputType.Textarea}
						label={translate(dict => dict.copyProject.newProjectDescription)}
						rows={4}
						placeholder={values.description.toString()}
						maxLength={240}
						onChange={e => calculateLetters(e)}
						readOnly={!isSameOwner}
						onBlur={handleTrimOnBlur}
					/>
					<Typography.Hint>{textAreaCount} / 240</Typography.Hint>
					<Spacer size={s => s.s} />
					<Row>
						<Column>
							<Input
								{...register('givenProjectNumber')}
								type={InputType.Text}
								label={translate(dict => dict.copyProject.newProjectNumber)}
								placeholder={values.givenProjectNumber.toString()}
								readOnly={!isSameOwner}
								onBlur={handleTrimOnBlur}
							/>
						</Column>
						<Column>
							<Controller
								control={control}
								name="projectEndDate"
								defaultValue={values.projectEndDate}
								render={({ field: { value } }) => (
									<Input
										type={InputType.Date}
										value={value as string}
										error={endDateError}
										label={translate(
											dict => dict.copyProject.newProjectEndDate
										)}
										onDateChange={({ formattedDate }) => {
											checkProjectEndDate(formattedDate);
											setValue('projectEndDate', formattedDate, {
												shouldDirty: true,
												shouldValidate: true
											});
										}}
										readOnly={!isSameOwner}
										onBlur={handleTrimOnBlur}
									/>
								)}
							/>
						</Column>
					</Row>
					<Spacer size={s => s.s} />
					<InputLabel
						label={translate(dict => dict.copyProject.changeOwner.projectOwner)}
					/>

					<Flex>
						<UserCard.Table
							userId={projectOwner.userId}
							userData={{
								...projectOwner,
								userSirName:
									projectOwner.userSirName +
									(projectOwner.userId === userId
										? ` (${translate(({ tags }) => tags.you)})`
										: '')
							}}
						/>
						<Button
							variant={v => v.link}
							title={translate(dict => dict.copyProject.changeOwner.changeOwner)}
							marginOffset={{ left: 1.6 }}
							onClick={() => setShowChangeOwnerModal(true)}
						/>
					</Flex>
					<Spacer size={s => s.xs} />
					<Typography.Hint>
						*
						{translate(
							dict => dict.copyProject.changeOwner.changeProjectDetailsRestriction
						)}
					</Typography.Hint>

					{formMetadataParameters.length > 0 && isProjectOwnerInHSO && (
						<>
							{formMetadataParameters.map((metadataParametersRow, i) => {
								return (
									<div key={i}>
										<Row>
											{metadataParametersRow.map(metadataParameter => (
												<Column key={metadataParameter.name}>
													{metadataParameter.type ===
														ProjectMetadataType.String && (
														<Input
															{...(canEditMetadata
																? register(
																		`${metadataParameter.name}`,
																		{
																			onBlur: canEditMetadata
																				? handleTrimOnBlur
																				: undefined
																		}
																  )
																: {})}
															placeholder={translate(
																() => metadataParameter.placeholder
															)}
															type={InputType.Text}
															readOnly={!isSameOwner}
															label={translate(
																() => metadataParameter.label
															)}
															error={
																errors[metadataParameter.name]
																	?.message
															}
															tooltipComponent={
																<InfoTooltip
																	marginOffset={{
																		left: 0.4,
																		bottom: 0.4
																	}}
																	iconVisible
																	text={translate(
																		() =>
																			metadataParameter.description
																	)}
																/>
															}
															required={requiredFields.has(
																metadataParameter.name
															)}
														/>
													)}

													{metadataParameter.type ===
														ProjectMetadataType.Dropdown && (
														<Controller
															name={`${metadataParameter.name}`}
															control={control}
															render={({
																field: { value, onBlur }
															}) => (
																<CreatableSelect
																	label={translate(
																		() =>
																			metadataParameter.label
																	)}
																	tooltipPlace="top"
																	tooltipComponent={
																		<InfoTooltip
																			marginOffset={{
																				left: 0.4,
																				bottom: 0.4
																			}}
																			iconVisible
																			text={translate(
																				() =>
																					metadataParameter.description
																			)}
																		/>
																	}
																	placeholder={translate(
																		() =>
																			metadataParameter.placeholder
																	)}
																	value={
																		canEditMetadata
																			? value
																				? {
																						value: value.toString(),
																						label: translate(
																							() =>
																								metadataParameter.categories.find(
																									p =>
																										p.id ===
																										value
																								)
																									?.name ??
																								''
																						)
																				  }
																				: undefined
																			: {
																					value: '3',
																					label: translate(
																						() =>
																							'projectMetadata.hso.projectType.testProject.label'
																					)
																			  }
																	}
																	items={metadataParameter.categories
																		.filter(i => i.active)
																		.map(option => ({
																			label: translate(
																				() => option.name
																			),
																			value: `${option.id}`
																		}))}
																	error={
																		errors[
																			metadataParameter.name
																		]?.message
																	}
																	onClear={() => {
																		setValue(
																			`${metadataParameter.name}`,
																			'',
																			{
																				shouldDirty: true
																			}
																		);
																	}}
																	borderError={
																		!!errors[
																			metadataParameter.name
																		]?.message
																	}
																	required={requiredFields.has(
																		metadataParameter.name
																	)}
																	onValueSelected={selectedValue => {
																		if (selectedValue) {
																			setValue(
																				`${metadataParameter.name}`,
																				selectedValue,
																				{
																					shouldDirty:
																						true
																				}
																			);
																		}
																	}}
																	readOnly={!isSameOwner}
																	hint={translate(
																		() =>
																			metadataParameter.categories.find(
																				p => p.id === value
																			)?.description ?? ''
																	)}
																	onBlur={onBlur}
																	scrollIntoView
																/>
															)}
														/>
													)}
												</Column>
											))}
										</Row>
									</div>
								);
							})}
						</>
					)}

					<Spacer size={s => s.l} />
					<InputLabel label={translate(dict => dict.copyProject.include)} />
					<Typography.Hint>
						{translate(dict => dict.copyProject.selectElement)}
					</Typography.Hint>
					<Spacer size={s => s.s} />
					<CheckboxContainer>
						{Object.keys(isElementChecked).map(
							elementKey =>
								isElementChecked[elementKey].visible && (
									<Checkbox
										marginOffset={{ bottom: 1 }}
										key={elementKey}
										className="checkbox"
										label={firstLetterToUpperCase(
											translate(
												dict =>
													dict.copyProject.copyOptions[
														elementKey as CheckboxOptions
													]
											)
										)}
										onClick={() => handleSelectedElement(elementKey)}
										checked={isElementChecked[elementKey].checked}
										disabled={isElementChecked[elementKey].disabled}
									/>
								)
						)}
					</CheckboxContainer>
				</Form>
				<StickyFooter
					primary={buttonProps.primary}
					neutral={formHasChanges && buttonProps.neutral}
					maxWidth={65.2}
					modalFooter={true}
				/>
			</FormContainer>

			{showChangeOwnerModal && (
				<ChangeOwnerModal
					projectId={projectId}
					currentOwnerEmail={projectOwner.emailAddress}
					onClose={newOwner => {
						setShowChangeOwnerModal(false);
						newOwner && setProjectOwner(newOwner);
					}}
				/>
			)}
		</Modal>
	);
}

function parseFormValuesToProject(
	values: ProjectFormValues,
	projectMetadataDefinition: ProjectMetadataDefinition
) {
	const project: CopyProjectDetails = {
		name: values.projectName?.toString() || undefined,
		givenNumber: values.givenProjectNumber?.toString() || undefined,
		description: values.description?.toString() || undefined,
		endDate: values.projectEndDate?.toString() || undefined
	};

	if (projectMetadataDefinition && projectMetadataDefinition.metadataDefinition) {
		projectMetadataDefinition.metadataDefinition.forEach(definition => {
			if (!project.metadata) project.metadata = {};
			project.metadata[definition.name] = values[definition.name]?.toString();
		});
	}

	project.metadata = parseToApiMetadata(project.metadata);
	return project;
}
